import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Pin, RefreshCw, Trash2, ChevronDown, ChevronUp, Clock, AlertCircle, Code } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { usePinnedCharts } from '../hooks/usePinnedCharts';
import { useVegaChart } from '../hooks/useVegaChart';
import { theme } from '../styles/theme';

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return 'غير معروف';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'الآن';
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
  if (diffHours < 24) return `منذ ${diffHours} ساعة`;
  if (diffDays < 7) return `منذ ${diffDays} يوم`;

  return date.toLocaleDateString('ar-EG');
};

const SectionContainer = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${theme.colors.borderLight};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.9;
  }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin: 0;
  direction: rtl;

  svg {
    width: 24px;
    height: 24px;
    color: #f59e0b;
  }
`;

const ChartCount = styled.span`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;

  &:hover {
    color: ${theme.colors.textPrimary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ChartsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(max(380px, calc(50% - 12px)), 1fr));
  gap: 24px;
`;

const ChartCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-width: 0;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${theme.colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin: 0;
  direction: rtl;
  text-align: right;
  flex: 1;
  line-height: 1.4;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled(motion.button)`
  background: ${props => props.$variant === 'danger'
    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    : props.$variant === 'refresh'
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
  };
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ChartContainer = styled.div`
  padding: 16px;
  width: 100%;
  min-height: 300px;
`;

const CardFooter = styled.div`
  padding: 12px 20px;
  background: ${theme.colors.backgroundMuted};
  border-top: 1px solid ${theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  direction: rtl;
`;

const TimeStamp = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${theme.colors.textMuted};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const OriginalQuestion = styled.div`
  font-size: 12px;
  color: ${theme.colors.textSecondary};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${theme.colors.textMuted};
  direction: rtl;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px;
    color: ${theme.colors.textSecondary};
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 24px;
  color: #ef4444;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
  }
`;

const SQLToggle = styled.button`
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  border-radius: 6px;
  transition: all 0.2s ease;
  direction: rtl;

  &:hover {
    background: ${theme.colors.backgroundMuted};
    color: ${theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SQLSection = styled(motion.div)`
  padding: 0 16px 16px;
  direction: rtl;
`;

const SQLContent = styled.div`
  background: #1a1b26;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .syntax-highlighter {
    margin: 0 !important;
    background: transparent !important;
    padding: 12px !important;
    font-size: 12px;
    line-height: 1.5;
    direction: ltr;
    text-align: left;

    pre {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }

    code {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }
  }
`;

// Individual pinned chart card component
const PinnedChartCard = ({ chart, onRefresh, onUnpin, isRefreshing }) => {
  const chartRef = useRef(null);
  const [showSql, setShowSql] = useState(false);

  // Root cause: the backend's chart_preprocessing.py forces orient:'bottom' +
  // symbolLimit:0 + columns:0 + labelLimit:300 on every color encoding before storage.
  // That polluted legend can't fit a narrow pinned-card. Fix: strip the legend key from
  // every visual channel so Vega uses its defaults (orient:'right', sane truncation) —
  // exactly like the MV dashboard charts in Dashboard.js which never set legend at all.
  const src = chart.chart_spec;

  const LEGEND_CHANNELS = new Set(['color', 'shape', 'size', 'fill', 'stroke', 'opacity']);
  const stripBackendLegends = (enc) => {
    if (!enc) return enc;
    return Object.fromEntries(
      Object.entries(enc).map(([ch, def]) => {
        if (!def || !LEGEND_CHANNELS.has(ch)) return [ch, def];
        if (def.legend === null) return [ch, def]; // respect explicit disable
        const { legend, ...rest } = def;
        return [ch, rest]; // drop the polluted legend key entirely
      })
    );
  };

  // Match Dashboard.js's spec shape: fresh spec from data/mark/encoding, width:'container'
  // + fixed height, minimal config, no autosize override, no legend config.
  const enhancedSpec = src ? {
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    ...(src.data      ? { data: src.data }                                : {}),
    ...(src.mark      ? { mark: src.mark }                                : {}),
    ...(src.encoding  ? { encoding: stripBackendLegends(src.encoding) }   : {}),
    ...(src.transform ? { transform: src.transform }                      : {}),
    ...(src.layer     ? { layer: src.layer }                              : {}),
    width:  'container',
    height: 280,
    config: {
      view: { stroke: null },
      axis: { labelFontSize: 11, titleFontSize: 12, labelLimit: 120, titlePadding: 8 },
      axisX: { labelAngle: -45, labelAlign: 'right', labelOverlap: 'parity' },
      // Cards are full-width single-column now — Vega's default right-side legend
      // fits cleanly without overlapping bars (same behavior as image #29 modal view).
    },
  } : null;

  useVegaChart(chartRef, enhancedSpec);

  return (
    <ChartCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      layout
    >
      <CardHeader>
        <ChartTitle>{chart.title}</ChartTitle>
        <CardActions>
          <ActionButton
            $variant="refresh"
            onClick={() => onRefresh(chart.id)}
            disabled={isRefreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="تحديث البيانات"
          >
            <RefreshCw className={isRefreshing ? 'spinner' : ''} />
          </ActionButton>
          <ActionButton
            $variant="danger"
            onClick={() => onUnpin(chart.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="إزالة من لوحة المعلومات"
          >
            <Trash2 />
          </ActionButton>
        </CardActions>
      </CardHeader>

      <ChartContainer>
        <div
          ref={chartRef}
          style={{
            width: '100%',
            minHeight: '280px',
            contain: 'layout',
            direction: 'ltr',
          }}
        />
      </ChartContainer>

      {/* SQL Toggle */}
      {chart.sql_query && (
        <div style={{ padding: '0 16px', borderTop: `1px solid ${theme.colors.borderLight}` }}>
          <SQLToggle onClick={() => setShowSql(!showSql)}>
            <Code />
            <span>عرض استعلام SQL</span>
            {showSql ? <ChevronUp /> : <ChevronDown />}
          </SQLToggle>
        </div>
      )}

      {/* Collapsible SQL Section */}
      <AnimatePresence>
        {showSql && chart.sql_query && (
          <SQLSection
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <SQLContent>
              <SyntaxHighlighter
                language="sql"
                style={tomorrow}
                className="syntax-highlighter"
                customStyle={{
                  margin: '0',
                  padding: '12px',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}
                wrapLongLines={true}
              >
                {chart.sql_query}
              </SyntaxHighlighter>
            </SQLContent>
          </SQLSection>
        )}
      </AnimatePresence>

      <CardFooter>
        <TimeStamp>
          <Clock />
          آخر تحديث: {formatTimeAgo(chart.last_refreshed_at || chart.updated_at)}
        </TimeStamp>
        {chart.original_question && (
          <OriginalQuestion title={chart.original_question}>
            {chart.original_question.substring(0, 50)}...
          </OriginalQuestion>
        )}
      </CardFooter>
    </ChartCard>
  );
};

const MyChartsSection = () => {
  const {
    pinnedCharts,
    loading,
    error,
    refreshingChartId,
    refreshChart,
    unpinChart
  } = usePinnedCharts();

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Don't render if no pinned charts and not loading
  if (!loading && pinnedCharts.length === 0) {
    return null;
  }

  const handleRefresh = async (chartId) => {
    const result = await refreshChart(chartId);
    if (!result.success) {
      console.error('Failed to refresh chart:', result.error);
    }
  };

  const handleUnpin = async (chartId) => {
    const result = await unpinChart(chartId);
    if (!result.success) {
      console.error('Failed to unpin chart:', result.error);
    }
  };

  return (
    <SectionContainer>
      <SectionHeader onClick={() => setIsCollapsed(!isCollapsed)}>
        <SectionTitle>
          <Pin />
          الرسوم البيانية المثبتة
          <ChartCount>{pinnedCharts.length}</ChartCount>
        </SectionTitle>
        <CollapseButton>
          {isCollapsed ? <ChevronDown /> : <ChevronUp />}
        </CollapseButton>
      </SectionHeader>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {loading ? (
              <EmptyState>
                <RefreshCw className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
                <h3>جاري التحميل...</h3>
              </EmptyState>
            ) : error ? (
              <ErrorState>
                <AlertCircle />
                <p>فشل في تحميل الرسوم البيانية: {error}</p>
              </ErrorState>
            ) : pinnedCharts.length === 0 ? (
              <EmptyState>
                <Pin />
                <h3>لا توجد رسوم بيانية مثبتة</h3>
                <p>قم بتثبيت الرسوم البيانية من المحادثات لعرضها هنا</p>
              </EmptyState>
            ) : (
              <ChartsGrid>
                <AnimatePresence mode="popLayout">
                  {pinnedCharts.map((chart) => (
                    <PinnedChartCard
                      key={chart.id}
                      chart={chart}
                      onRefresh={handleRefresh}
                      onUnpin={handleUnpin}
                      isRefreshing={refreshingChartId === chart.id}
                    />
                  ))}
                </AnimatePresence>
              </ChartsGrid>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </SectionContainer>
  );
};

export default MyChartsSection;
